import { type ApiEnvelope, api, unwrap } from "@/lib/api";

export type PushStatus = "draft" | "scheduled" | "sending" | "sent" | "failed";

export interface PushCampaign {
	id: string;
	title: string;
	message: string;
	deepLink: string | null;
	segmentId: string | null;
	segmentLabel: string | null;
	status: PushStatus;
	scheduledAt: string | null;
	sentAt: string | null;
	recipients: number;
	sentCount: number;
	failedCount: number;
	clickCount: number;
	clickRate: number;
	lastError: string | null;
	updatedAt: string;
	createdAt: string;
}

export interface PushStats {
	devices: { android: number; ios: number; web: number; total: number };
	optin: number;
	sentCampaigns: number;
	clickRate: number;
	/** Faux tant que la clé de service FCM n'est pas posée. */
	senderReady: boolean;
}

export const pushApi = {
	list: (params?: { status?: PushStatus }) =>
		unwrap<{ items: PushCampaign[]; total: number }>(
			api.get<ApiEnvelope<{ items: PushCampaign[]; total: number }>>(
				"/admin/push/campaigns",
				{ params },
			),
		),

	stats: () => unwrap<PushStats>(api.get<ApiEnvelope<PushStats>>("/admin/push/stats")),

	detail: (id: string) =>
		unwrap<PushCampaign>(
			api.get<ApiEnvelope<PushCampaign>>(`/admin/push/campaigns/${id}`),
		),

	create: (payload: {
		title: string;
		message: string;
		deepLink?: string;
		segmentId?: string;
		scheduledAt?: string;
	}) =>
		unwrap<PushCampaign>(
			api.post<ApiEnvelope<PushCampaign>>("/admin/push/campaigns", payload),
		),

	update: (
		id: string,
		payload: {
			title?: string;
			message?: string;
			deepLink?: string | null;
			segmentId?: string | null;
			scheduledAt?: string | null;
		},
	) =>
		unwrap<PushCampaign>(
			api.patch<ApiEnvelope<PushCampaign>>(
				`/admin/push/campaigns/${id}`,
				payload,
			),
		),

	schedule: (id: string, at?: string) =>
		unwrap<PushCampaign>(
			api.post<ApiEnvelope<PushCampaign>>(
				`/admin/push/campaigns/${id}/schedule`,
				at ? { at } : {},
			),
		),

	cancel: (id: string) =>
		unwrap<PushCampaign>(
			api.post<ApiEnvelope<PushCampaign>>(`/admin/push/campaigns/${id}/cancel`),
		),

	test: (id: string, token: string) =>
		unwrap<{ sent: boolean }>(
			api.post<ApiEnvelope<{ sent: boolean }>>(
				`/admin/push/campaigns/${id}/test`,
				{ token },
			),
		),

	remove: (id: string) =>
		unwrap<{ deleted: boolean }>(
			api.delete<ApiEnvelope<{ deleted: boolean }>>(
				`/admin/push/campaigns/${id}`,
			),
		),
};

export const PUSH_STATUS_LABELS: Record<
	PushStatus,
	{ label: string; variant: "success" | "info" | "neutral" | "danger" }
> = {
	sent: { label: "Envoyée", variant: "success" },
	sending: { label: "En cours", variant: "info" },
	scheduled: { label: "Programmée", variant: "info" },
	draft: { label: "Brouillon", variant: "neutral" },
	failed: { label: "Échouée", variant: "danger" },
};
